/**
 * FreeCryoGLO (Skin Quiz): show the free CryoGLO Roller Gift in the cart, not only at checkout.
 *
 * The gift itself is a Kite "Free Gift Checkout Discount". Kite adds the gift line in checkout with the
 * properties below, and its discount makes any line carrying them free, in the cart as well. This script
 * adds that same line as soon as the code is on the cart and the cart reaches the threshold, and takes it
 * out again when the cart no longer qualifies. If the line is not actually free (for example after the
 * Kite campaign ends) it is removed straight away, so nobody is charged for the "gift".
 */
(() => {
  const CODE = 'FREECRYOGLO';
  const GIFT_VARIANT_ID = 52980502102247; // CryoGLO Roller Gift (unlisted)
  const THRESHOLD_CENTS = 30000; // Kite: cart subtotal after discounts >= $300
  // Kite only makes the line free when it carries the campaign's current tier id, and Kite makes a new id
  // every time the campaign is saved. Start with the last known id; if the gift does not come out free,
  // read the current id from Kite's config in a fresh page and try once more.
  const TIER_KEY = 'rmd-freecryoglo-tier';
  const FALLBACK_TIER_ID = '3ydEeZHV'; // as of 2026-11-04
  let tierId = sessionStorage.getItem(TIER_KEY) || FALLBACK_TIER_ID;
  let tierRefreshed = false;
  const giftProperties = () => ({
    _free_product_checkout: 'true',
    _free_gift_tier_id: tierId,
    _kite_promo_name: CODE,
  });
  const FAILED_KEY = 'rmd-freecryoglo-failed';
  const CART_URL = /\/cart(\/(add|change|update|clear))?(\.js)?(\?|$)|\/discount\//;

  let running = false;
  let queued = false;
  let timer = null;
  let ownRequest = 0;

  const isGift = (item) => item.variant_id === GIFT_VARIANT_ID && item.properties?._kite_promo_name === CODE;
  const hasCode = (cart) => (cart.discount_codes || []).some((d) => String(d.code).toUpperCase() === CODE);

  const request = async (path, body) => {
    ownRequest += 1;
    try {
      const response = await fetch(path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) throw new Error(`${path} ${response.status}`);
      return response.json();
    } finally {
      ownRequest -= 1;
    }
  };

  const getCart = () => request('/cart.js');

  // Kite clears its config from the live page, so read it from a fresh copy of a small storefront page
  const fetchTierId = async () => {
    const html = await (await fetch('/pages/rmd-kite-config', { credentials: 'same-origin' })).text();
    const match = html.match(/<script id="kite-app-data" type="application\/json">([\s\S]*?)<\/script>/);
    if (!match) return null;
    const campaign = (JSON.parse(match[1]).freeGiftCheckout || []).find(
      (c) => String(c.title).toUpperCase() === CODE && c.isEnabled !== false && !c.isDeleted
    );
    const segment = (campaign?.segmentsData?.segmentsList || []).find((seg) => Number(seg.discountValue) > 0);
    return segment?.segmentId || null;
  };
  const setQuantities = (updates) => request('/cart/update.js', { updates });

  // Ask the cart drawer (Rebuy Smart Cart) and the theme's cart page to show the new cart
  const refreshCartUi = async (cart) => {
    try { await window.Rebuy?.Cart?.fetchCart?.(); } catch (error) { /* drawer refreshes on its own next open */ }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
    if (/^\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  };

  const sync = async () => {
    if (running) { queued = true; return; }
    running = true;
    try {
      let cart = await getCart();
      const gifts = cart.items.filter(isGift);
      const others = cart.items.filter((item) => !isGift(item));
      const subtotal = others.reduce((sum, item) => sum + item.final_line_price, 0);
      const signature = `${others.map((i) => `${i.key}:${i.quantity}`).join(',')}|${subtotal}`;
      const eligible = hasCode(cart) && subtotal >= THRESHOLD_CENTS && others.length > 0;
      const failedFor = sessionStorage.getItem(FAILED_KEY);

      if (!eligible || failedFor === signature) {
        if (gifts.length) {
          cart = await setQuantities(Object.fromEntries(gifts.map((g) => [g.key, 0])));
          await refreshCartUi(cart);
        }
        return;
      }

      if (!gifts.length) {
        await request('/cart/add.js', { items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: giftProperties() }] });
      } else if (gifts.length > 1 || gifts[0].quantity !== 1) {
        const updates = Object.fromEntries(gifts.map((g, i) => [g.key, i === 0 ? 1 : 0]));
        await setQuantities(updates);
      } else if (gifts[0].final_line_price === 0) {
        return; // already in the cart and free
      }

      cart = await getCart();
      let gift = cart.items.find(isGift);
      if (gift && gift.final_line_price > 0 && !tierRefreshed) {
        // maybe the campaign was saved again: pick up the new tier id and swap the line once
        tierRefreshed = true;
        const fresh = await fetchTierId().catch(() => null);
        if (fresh && fresh !== gift.properties?._free_gift_tier_id) {
          tierId = fresh;
          sessionStorage.setItem(TIER_KEY, fresh);
          await setQuantities({ [gift.key]: 0 });
          await request('/cart/add.js', { items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: giftProperties() }] });
          cart = await getCart();
          gift = cart.items.find(isGift);
        }
      }
      if (gift && gift.final_line_price > 0) {
        // Kite did not make it free: never leave a paid "gift" in the cart
        sessionStorage.setItem(FAILED_KEY, signature);
        cart = await setQuantities({ [gift.key]: 0 });
      } else {
        sessionStorage.removeItem(FAILED_KEY);
      }
      await refreshCartUi(cart);
    } catch (error) {
      console.warn('[FreeCryoGLO]', error);
    } finally {
      running = false;
      if (queued) { queued = false; schedule(); }
    }
  };

  const schedule = (delay = 500) => {
    clearTimeout(timer);
    timer = setTimeout(sync, delay);
  };

  // Re-check after any cart change made by the theme, Rebuy, or other apps
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const own = ownRequest > 0;
    const result = originalFetch.apply(this, arguments);
    if (!own && CART_URL.test(url) && (init?.method || 'GET').toUpperCase() !== 'GET') {
      result.then(() => schedule(), () => {});
    }
    return result;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (String(method).toUpperCase() !== 'GET' && CART_URL.test(String(url))) {
      this.addEventListener('load', () => schedule());
    }
    return originalOpen.apply(this, arguments);
  };

  ['rebuy:cart.change', 'rebuy:smartcart.show', 'cart:updated'].forEach((name) => {
    document.addEventListener(name, () => schedule());
  });
  window.addEventListener('pageshow', () => schedule(0));
})();
